// StreamViewTool2.jsx
// This component allows users to stream the current view of a renderer in real-time.

// Copyright HS Analysis GmbH, 2025
// Author: Viktor Eberhardt

// Framework imports
import React, { useState } from "react";
import PropTypes from "prop-types";

// MUI imports
import {
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CircularProgress from "@mui/material/CircularProgress"; // Import spinner component

// HSA imports
import streamManager from "./streamManager";

const StreamViewTool2 = ({ rendererDict, fileId, name }) => {
  const [isStreaming, setIsStreaming] = useState(streamManager.isStreaming());
  const [isStreamLive, setIsStreamLive] = useState(streamManager.isLive());
  const [streamUrl, setStreamUrl] = useState("");
  const [streamHeadline, setStreamHeadline] = useState("Stream of HSA KIT");
  const [streamMouseCursor, setStreamMouseCursor] = useState(
    () => localStorage.getItem("hsaStreamDrawMouseCursor") === "1"
  );
  const browserUUID = React.useRef("");
  const isStreamingRef = React.useRef(isStreaming);
  const prevRendererRef = React.useRef(null);
  const isMountedRef = React.useRef(true); // Track mount state to avoid setState on unmounted

  React.useEffect(() => {
    let storedBrowserUUID = localStorage.getItem("browserUUID");
    if (!storedBrowserUUID) {
      storedBrowserUUID =
        "id-" +
        Date.now().toString(36) +
        "-" +
        Math.random().toString(36).slice(2, 10);
      localStorage.setItem("browserUUID", storedBrowserUUID);
    }
    browserUUID.current = storedBrowserUUID;
    console.log("set browserUUID", browserUUID.current);
    setStreamUrl(
      (
        "https://stream.hs-analysis.com?target=" +
        storedBrowserUUID +
        "&name=" +
        name
      ).replace(/ /g, "_")
    );
    // Mark as mounted for safe state updates
    isMountedRef.current = true;
    return () => {
      // Do not stop streaming on unmount; just avoid future state updates
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Captures the current content of the renderer's canvas as a JPEG image,
   * converts it to a Blob, and streams it to the backend server if streaming is active.
   * Continues to upload images at regular intervals (every 100ms) while streaming is enabled.
   * Updates the stream live state and last upload timestamp accordingly.
   */
  // Keep the manager pointing to the current renderer for the active file
  React.useEffect(() => {
    // Use a dynamic provider that resolves the renderer on demand for the current fileId
    streamManager.setRendererProvider(() => {
      if (!rendererDict || !fileId) return null;
      return rendererDict[fileId] || null;
    });
  }, [rendererDict, fileId]);

  // Define the custom cursor drawer used for streaming
  const drawCursorFn = React.useRef(null);
  if (!drawCursorFn.current) {
    drawCursorFn.current = (ctx, mousePos, pixelScale /*, canvas */) => {
      if (!mousePos) return;
      const s = pixelScale || 1; // allow < 1 when zoomed out
      const inv = 1 / s; // convert screen px -> world units

      // Visuals: triangular mouse-pointer shape, tip at mousePos, oriented up-left
      const COLOR = "#0673C1";

      // Base dimensions in screen pixels (screen-constant via inv)
      // Make an isosceles pointer: two long sides equal, left long side vertical; base shorter but not too thin
      const len = 39 * inv; // length of both long sides (50% bigger)
      // Make the angle between long sides ~45°: set right side at 45° from vertical
      const dx = len / Math.SQRT2;
      const dy = len / Math.SQRT2;

      const x = mousePos.x;
      const y = mousePos.y;

      // Triangle points (tip at p0)
      // Left border vertical: p0 -> p1
      // Right long side matches length: p0 -> p2 with |p0p2|=len
      // Base is short: p1 -> p2
      const p0x = x; // tip
      const p0y = y;
      const p1x = x; // bottom of vertical edge (left)
      const p1y = y + len;
      const p2x = x + dx; // right vertex
      const p2y = y + dy; // chosen to keep equal side length

      ctx.save();
      ctx.globalAlpha = 0.98;

      // Closed polygon with two long sides (p0->p1 and p0->p2)
      // connected by two short lines meeting at an inward corner between the long sides.
      // Compute an inward corner near the midpoint of the base (p1<->p2)
      const mx = (p1x + p2x) / 2;
      const my = (p1y + p2y) / 2;
      const ux = p0x - mx;
      const uy = p0y - my;
      const ulen = Math.hypot(ux, uy) || 1;
      const indent = 10 * inv; // inward offset in screen pixels
      const pcx = mx + (ux / ulen) * indent;
      const pcy = my + (uy / ulen) * indent;

      // Draw polygon with true tip (p0) to avoid the appearance of a cut tip.
      // Start at pc so the closing join is away from the top-left edge.
      ctx.beginPath();
      ctx.moveTo(pcx, pcy);
      ctx.lineTo(p2x, p2y);
      ctx.lineTo(p0x, p0y);
      ctx.lineTo(p1x, p1y);
      ctx.closePath();
      ctx.fillStyle = COLOR;
      ctx.fill();

      // Add a white border, then a thinner blue stroke for crisp edges
      // Use miter joins and butt caps to avoid rounded corners
      ctx.lineJoin = "miter";
      ctx.lineCap = "butt";
      ctx.miterLimit = 50;
      ctx.strokeStyle = "#FFFFFF";
      ctx.lineWidth = 3.5 * inv;
      ctx.stroke();
      ctx.lineJoin = "miter";
      ctx.lineCap = "butt";
      ctx.miterLimit = 50;
      ctx.strokeStyle = COLOR;
      ctx.lineWidth = 2.0 * inv;
      ctx.stroke();

      ctx.restore();
    };
  }

  // Apply/remove custom cursor on renderer when toggled or when active file's renderer becomes available
  React.useEffect(() => {
    let cancelled = false;

    const detachFrom = (r) => {
      if (!r || typeof r !== "object") return;
      try {
        delete r.drawCustomCursor;
      } catch (_) {
        r.drawCustomCursor = undefined;
      }
      if (r.__hsaStream_forceRender) {
        r.keepRendering = r.__hsaStream_prevKeepRendering ?? false;
        delete r.__hsaStream_forceRender;
        delete r.__hsaStream_prevKeepRendering;
      }
    };

    const attachTo = (r) => {
      if (!r || typeof r !== "object") return;
      r.drawCustomCursor = drawCursorFn.current;
      if (!r.__hsaStream_forceRender) {
        r.__hsaStream_prevKeepRendering = r.keepRendering;
      }
      r.__hsaStream_forceRender = true;
      r.keepRendering = true;
      if (typeof r.draw === "function") {
        try {
          r.draw();
        } catch (e) {
          // ignore draw errors; loop will retry on next frame
        }
      }
    };

    const tryApply = () => {
      const current = rendererDict && fileId ? rendererDict[fileId] : null;
      const prev = prevRendererRef.current;

      if (prev && prev !== current) {
        detachFrom(prev);
      }

      if (current) {
        if (streamMouseCursor) attachTo(current);
        else detachFrom(current);
        prevRendererRef.current = current;
        return true;
      }
      return false;
    };

    // Immediate attempt; if renderer not ready yet, retry shortly until it appears or the effect is cleaned up
    if (!tryApply()) {
      let attempts = 0;
      const maxAttempts = 60; // ~1 sec with rAF
      const tick = () => {
        if (cancelled) return;
        if (tryApply()) return;
        attempts += 1;
        if (attempts < maxAttempts) {
          requestAnimationFrame(tick);
        }
      };
      requestAnimationFrame(tick);
    }

    return () => {
      cancelled = true;
    };
  }, [rendererDict, fileId, streamMouseCursor]);

  // Persist user preference
  React.useEffect(() => {
    localStorage.setItem(
      "hsaStreamDrawMouseCursor",
      streamMouseCursor ? "1" : "0"
    );
  }, [streamMouseCursor]);

  /**
   * Toggles the streaming state.
   *
   * Updates the `isStreaming` state and its corresponding ref (`isStreamingRef.current`).
   * If streaming is enabled, triggers the `uploadRendererContent` function.
   */
  const toggleStream = () => {
    if (!isStreamingRef.current) {
      // Start manager; it will keep streaming across component remounts/file switches
      streamManager.start({
        browserUUID: browserUUID.current,
        rendererProvider: () =>
          rendererDict && fileId ? rendererDict[fileId] || null : null,
      });
    } else {
      streamManager.stop();
    }
  };

  /**
   * Copies the current stream URL to the clipboard and displays a success snackbar notification.
   */
  const handleCopyLink = () => {
    navigator.clipboard.writeText(streamUrl);
    window.showSuccessSnackbar("Stream link copied to clipboard!");
  };

  // Subscribe to manager state so the UI reflects true streaming state
  React.useEffect(() => {
    const unsubStream = streamManager.subscribe((val) => {
      isStreamingRef.current = val;
      if (isMountedRef.current) setIsStreaming(val);
    });
    const unsubLive = streamManager.subscribeLive((live) => {
      if (isMountedRef.current) setIsStreamLive(live);
    });
    return () => {
      unsubStream();
      unsubLive();
    };
  }, []);

  React.useEffect(() => {
    const encodedHeadline = encodeURIComponent(streamHeadline);
    setStreamUrl(
      `https://stream.hs-analysis.com?target=${browserUUID.current}&name=${encodedHeadline}`
    );
  }, [streamHeadline, browserUUID]);

  return (
    <div>
      <Typography variant="h6">
        <span style={{ position: "relative", top: "-6px", marginRight: "8px" }}>
          Stream
        </span>
        {isStreaming && !isStreamLive && (
          <CircularProgress size={24} style={{ marginRight: "10px" }} />
        )}
      </Typography>

      <FormControl
        fullWidth
        sx={{ m: 1, margin: "0 0 10px 0" }}
        variant="outlined"
      >
        <InputLabel htmlFor="outlined-adornment-password">
          Stream Headline
        </InputLabel>
        <OutlinedInput
          fullWidth
          type="text"
          value={streamHeadline}
          onChange={(e) => {
            setStreamHeadline(e.target.value);
          }}
          label="Stream Headline"
        />
      </FormControl>
      <FormControl fullWidth sx={{ m: 1, margin: 0 }} variant="outlined">
        <InputLabel htmlFor="outlined-adornment-password">
          Stream link
        </InputLabel>
        <OutlinedInput
          fullWidth
          type="text"
          value={streamUrl}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="copy link to clipboard"
                onClick={handleCopyLink}
                edge="end"
              >
                <ContentCopyIcon />
              </IconButton>
            </InputAdornment>
          }
          label="Stream link"
        />
      </FormControl>
      <FormControlLabel
        control={
          <Checkbox
            color="primary"
            checked={streamMouseCursor}
            onChange={(e) => setStreamMouseCursor(e.target.checked)}
          />
        }
        label="Stream mouse cursor"
        sx={{ mt: 1 }}
      />
      <Button
        style={{ marginTop: "5px" }}
        fullWidth
        variant="contained"
        color="primary"
        onClick={toggleStream}
      >
        {isStreaming ? <StopIcon /> : <PlayArrowIcon />}
        <span style={{ marginTop: 2 }}>
          {isStreaming ? "Stop Stream" : "Start Stream"}
        </span>
      </Button>
    </div>
  );
};

StreamViewTool2.propTypes = {
  rendererDict: PropTypes.object.isRequired,
  fileId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
};

export default StreamViewTool2;
